import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard, TrendingUp, Scale, Clock,
  Calendar, Receipt, Building2, User, HelpCircle, LogOut,
  ChevronLeft, ChevronRight, Sparkles, FileText, Zap,
  Lock, X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePlans } from '@/contexts/PlanRulesContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const secondaryItems = [
  { id: 'invoices', label: 'Subscription & Invoices', icon: Receipt },
  { id: 'compset', label: 'CompSet', icon: Building2 },
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'help', label: 'How it works', icon: HelpCircle },
];

export default function DashboardSidebar({ activeView, setActiveView, collapsed, setCollapsed }) {
  const { canAccessRateParity, canAccessAdvanceAnalysis, canCreateSchedule, canAccessAiInsights } = usePlans();
  const { handleLogout } = useAuth();

  // true  when viewport < 768 px  (phones)
  const isMobile = useMediaQuery('(max-width: 767px)');

  const navItems = [
    { id: 'market-insights', label: 'Market Insights', icon: LayoutDashboard, badge: null, hasAccess: true },
    { id: 'competitive-analysis', label: 'Competitive Analysis', icon: TrendingUp, badge: canAccessAiInsights() ? 'AI' : null, hasAccess: true },
    { id: 'rate-parity', label: 'Rate Parity', icon: Scale, hasAccess: canAccessRateParity() },
    { id: 'advanced-analytics', label: 'Advanced Analytics', icon: Zap, hasAccess: canAccessAdvanceAnalysis() },
    { id: 'reports', label: 'Reports & Intelligence', icon: FileText, hasAccess: true },
    { id: 'run-logs', label: 'Run Logs', icon: Clock, badge: null, hasAccess: true },
    { id: 'scheduled-run', label: 'Scheduled Run', icon: Calendar, badge: null, hasAccess: canCreateSchedule() },
  ];

  const handleNavClick = (id) => {
    setActiveView(id);
    if (isMobile) setCollapsed(true); // auto-close drawer on mobile
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // MOBILE  →  Full-width slide-in drawer (translateX)
  //            collapsed = true  → -translate-x-full  (hidden off-screen left)
  //            collapsed = false → translate-x-0       (visible, overlays content)
  // ─────────────────────────────────────────────────────────────────────────────
  if (isMobile) {
    const isOpen = !collapsed;

    return (
      <>
        {/* Tap-outside backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setCollapsed(true)}
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed left-0 top-0 h-full w-72 z-50
            bg-card border-r border-border shadow-2xl
            flex flex-col
            transition-transform duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Logo + close */}
          <div className="p-4 flex items-center justify-between border-b border-border shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-primary-foreground shrink-0">
                P
              </div>
              <span className="font-heading font-semibold text-lg text-foreground">Prowen</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setCollapsed(true)}
              aria-label="Close sidebar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
              Analytics
            </p>
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant={activeView === item.id ? 'secondary' : 'ghost'}
                className={`w-full justify-start gap-3 h-10 ${activeView === item.id ? 'bg-primary/10 text-primary' : ''}`}
                onClick={() => handleNavClick(item.id)}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className={`flex-1 text-left ${!item.hasAccess ? 'text-gray-500' : ''}`}>
                  {item.label}
                </span>
                {!item.hasAccess && (
                  <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <Lock className="h-3 w-3 mr-0.5" />
                  </Badge>
                )}
                {item.hasAccess && item.badge && (
                  <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-accent/10 text-accent border-accent/30">
                    <Sparkles className="h-3 w-3 mr-0.5" />
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}

            <Separator className="my-4" />

            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
              Settings
            </p>
            {secondaryItems.map((item) => (
              <Button
                key={item.id}
                variant={activeView === item.id ? 'secondary' : 'ghost'}
                className={`w-full justify-start gap-3 h-10 ${activeView === item.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground'
                  }`}
                onClick={() => handleNavClick(item.id)}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
              </Button>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-border shrink-0">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              <span>Logout</span>
            </Button>
          </div>
        </aside>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // TABLET / DESKTOP  →  icon rail (w-16) ↔ full sidebar (w-64)
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <aside
      className={`
        fixed left-0 top-0 h-full bg-card border-r border-border z-40
        transition-all duration-300 flex flex-col
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Logo */}
      <div className="p-4 flex items-center justify-between border-b border-border shrink-0">
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center w-full' : ''}`}>
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-primary-foreground shrink-0">
            P
          </div>
          {!collapsed && (
            <span className="font-heading font-semibold text-lg text-foreground">Prowen</span>
          )}
        </div>
        {!collapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Expand toggle (rail mode) */}
      {collapsed && (
        <Button
          variant="ghost"
          size="icon"
          className="mx-auto mt-2 h-8 w-8 shrink-0"
          onClick={() => setCollapsed(false)}
          aria-label="Expand sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {!collapsed && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
            Analytics
          </p>
        )}
        {navItems.map((item) => (
          <Button
            key={item.id}
            variant={activeView === item.id ? 'secondary' : 'ghost'}
            className={`
              w-full justify-start gap-3 h-10
              ${collapsed ? 'px-0 justify-center' : ''}
              ${activeView === item.id ? 'bg-primary/10 text-primary' : ''}
            `}
            onClick={() => handleNavClick(item.id)}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className={`flex-1 text-left ${!item.hasAccess ? 'text-gray-500' : ''}`}>
                  {item.label}
                </span>
                {!item.hasAccess && (
                  <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <Lock className="h-3 w-3 mr-0.5" />
                  </Badge>
                )}
                {item.hasAccess && item.badge && (
                  <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-accent/10 text-accent border-accent/30">
                    <Sparkles className="h-3 w-3 mr-0.5" />
                    {item.badge}
                  </Badge>
                )}
              </>
            )}
          </Button>
        ))}

        <Separator className="my-4" />

        {!collapsed && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
            Settings
          </p>
        )}
        {secondaryItems.map((item) => (
          <Button
            key={item.id}
            variant={activeView === item.id ? 'secondary' : 'ghost'}
            className={`
              w-full justify-start gap-3 h-10
              ${collapsed ? 'px-0 justify-center' : ''}
              ${activeView === item.id ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}
            `}
            onClick={() => handleNavClick(item.id)}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Button>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-border shrink-0">
        <Button
          variant="ghost"
          className={`
            w-full justify-start gap-3 h-10 text-muted-foreground hover:text-destructive
            ${collapsed ? 'px-0 justify-center' : ''}
          `}
          onClick={handleLogout}
          data-testid="logout-btn"
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
    </aside>
  );
}